import { createAsyncThunk } from '@reduxjs/toolkit'
import RestClient from '../../../../services/client/RestClient'
import Configuration from '../../../../services/api/Configuration'
import { addToArrIfNotExistOrRemove, isNotEmpty } from '../../../../utils/helpers'

const { Customer_Admin_Users_Endpoints } = Configuration

export const customerAdminResetAlertHandler = createAsyncThunk(
  'customerAdmin/customerAdminResetAlertHandler',
  async () => ({}),
)

export const customerAdminRetrieveUsers = createAsyncThunk(
  'customerAdmin/getUsers',
  async (_, { dispatch }) => {
    const response = await RestClient.getRequest(`${Customer_Admin_Users_Endpoints.list}`)

    const { payload } = await dispatch(
      fixFacetsUserTypes({ queries: response.data.facets.user_types }),
    )

    return {
      data: response.data,
      userTypes: payload,
    }
  },
)

export const customerAdminFilterBySummary = createAsyncThunk(
  'customerAdmin/usersBySummary',
  async ({ queries }, { getState, dispatch }) => {
    const { customerAdminUsers } = getState()
    const response = await RestClient.getRequest(
      `${Customer_Admin_Users_Endpoints.list}?search=${customerAdminUsers.activeSearch}&data_status=${queries}&sort_by=${customerAdminUsers.activeSort.key}`,
    )

    const { payload } = await dispatch(
      fixFacetsUserTypes({ queries: response.data.facets.user_types }),
    )

    return { data: response.data, summary: queries, userTypes: payload }
  },
)

export const customerAdminSearchUsers = createAsyncThunk(
  'customerAdmin/searchUsers',
  async ({ queries }, { dispatch }) => {
    const response = await RestClient.getRequest(`${Customer_Admin_Users_Endpoints.list}?search=${queries || ''}`)

    const { payload } = await dispatch(
      fixFacetsUserTypes({ queries: response.data.facets.user_types }),
    )

    return {
      data: response.data,
      search: queries || '',
      userTypes: payload,
    }
  },
)

export const customerAdminUsersSortBy = createAsyncThunk(
  'customerAdmin/usersSortBy',
  async ({ queries }, { getState, dispatch }) => {
    const { customerAdminUsers } = getState()

    const response = await RestClient.getRequest(
      `${Customer_Admin_Users_Endpoints.list}?search=${customerAdminUsers.activeSearch}&data_status=${customerAdminUsers.activeSummary}&sort_by=${queries.key}`,
    )

    const { payload } = await dispatch(
      fixFacetsUserTypes({ queries: response.data.facets.user_types }),
    )

    return { data: response.data, sort: queries, userTypes: payload }
  },
)

export const customerAdminUsersPagination = createAsyncThunk(
  'customerAdmin/usersPagination',
  async ({ queries }, { getState, dispatch }) => {
    const { customerAdminUsers } = getState()

    const response = await RestClient.getRequest(
      `${Customer_Admin_Users_Endpoints.list}?search=${customerAdminUsers.activeSearch}&page_number=${queries.pageNumber}&data_status=${customerAdminUsers.activeSummary}&sort_by=${customerAdminUsers.activeSort.key}`,
    )

    const { payload } = await dispatch(
      fixFacetsUserTypes({ queries: response.data.facets.user_types }),
    )

    return { data: response.data, pageNumber: queries.pageNumber, userTypes: payload }
  },
)

export const customerAdminGetByUserTypes = createAsyncThunk(
  'customerAdmin/customerAdminGetByUserTypes',
  async ({ queries }, { getState }) => {
    const { customerAdminUsers } = getState()

    let newFilterArr

    if (queries === 'clear') {
      newFilterArr = []
    } else if (Array.isArray(queries)) {
      newFilterArr = queries
    } else {
      newFilterArr = addToArrIfNotExistOrRemove(customerAdminUsers.activeFilters, 'id', queries)
    }

    let userTypesReq = ''

    newFilterArr.forEach((singleType) => {
      userTypesReq = userTypesReq.concat(`&user_types=${singleType.id}`)
    })

    const response = await RestClient.getRequest(
      `${Customer_Admin_Users_Endpoints.list}?search=${customerAdminUsers.activeSearch}&data_status=${customerAdminUsers.activeSummary}${userTypesReq}`,
    )

    return { data: response.data, activeFilter: newFilterArr }
  },
)

const fixFacetsUserTypes = createAsyncThunk(
  'customerAdmin/fixFacetsUserTypes',
  async ({ queries }) => {
    const userTypes = []

    queries.forEach((singleUserType) => {
      userTypes.push({
        id: singleUserType.key,
        name: singleUserType.value,
        checked: false,
      })
    })

    return userTypes
  },
)

export const customerAdminRetrieveUserByID = createAsyncThunk(
  'customerAdminUsers/getUserById',
  async ({ queries }, { rejectWithValue }) => {
    const res = await RestClient.getRequest(`${Customer_Admin_Users_Endpoints.list}/${queries.id}`)

    if (res.status >= 300) {
      return rejectWithValue(res)
    }

    return res.data
  },
)

export const customerAdminUserChangeStatus = createAsyncThunk(
  'sysAdminUsers/changeStatus',
  async ({ queries }) => {
    const response = await RestClient.putRequest(`${Customer_Admin_Users_Endpoints.list}/${queries.id}`, {
      active: queries.status,
    })

    return response.data
  },
)

export const customerAdminUpdateSelectedUser = createAsyncThunk(
  'customerAdmin/updateSelectedUser',
  async ({ queries }) => queries,
)

export const customerAdminEditUser = createAsyncThunk(
  'customerAdmin/editUser',
  async ({ queries }, { rejectWithValue, getState }) => {
    const titleId = { title_id: queries.updatedUser?.title?.id }
    let employee = null

    if (isNotEmpty(queries.updatedUser.employee)) {
      employee = {
        code: queries.updatedUser.employee,
        departments: queries.updatedUser.departments,
      }
    }

    const updatedUserClone = { ...queries.updatedUser }
    delete updatedUserClone.title

    const newQuery = {
      ...updatedUserClone,
      ...titleId,
      employee: queries.updatedUser.employee?.id ? queries.updatedUser.employee : employee,
    }

    const res = await RestClient.putRequest(`${Customer_Admin_Users_Endpoints.list}/${queries.updatedUser.id}`, newQuery)

    if (res.status >= 300) {
      return rejectWithValue(res)
    }

    const { language } = getState()

    return {
      severity: 'success',
      message: language?.translations?.['User has been updated successfully'],
    }
  },
)

export const customerAdminCreateUsers = createAsyncThunk(
  'customerAdmin/createUsers',
  async ({ queries }, { rejectWithValue, getState }) => {
    const titleId = { title_id: queries.updatedUser?.title?.id }
    let employee = null

    if (isNotEmpty(queries.updatedUser.employee)) {
      employee = {
        code: queries.updatedUser.employee,
        departments: queries.updatedUser.departments,
      }
    }

    const updatedUserClone = { ...queries.updatedUser }
    delete updatedUserClone.title

    const newQuery = {
      ...updatedUserClone,
      ...titleId,
      employee: queries.updatedUser.employee?.id ? queries.updatedUser.employee : employee,
    }

    const res = await RestClient.postRequest(`${Customer_Admin_Users_Endpoints.list}`, newQuery)

    if (res.status >= 300) {
      return rejectWithValue(res)
    }

    const { language } = getState()

    return {
      severity: 'success',
      message: language?.translations?.['User has been added successfully'],
    }
  },
)

export const getCountForAllNonRegisterUsers = createAsyncThunk(
  'customerAdminUsers/getCountForAllNonRegisterUsers',
  async (_, { rejectWithValue }) => {
    const res = await RestClient
      .getRequest(`${process.env.REACT_APP_CORE_FRONTEND_URL}/otms-rest/api/${Customer_Admin_Users_Endpoints.getCountForAllNonRegisterUsers}`)

    if (res.status >= 300) {
      return rejectWithValue(res)
    }

    return res.data
  },
)

export const postBulkCreateForAllUsers = createAsyncThunk(
  'customerAdminUsers/postBulkCreateForAllUsers',
  async ({ queries }, { rejectWithValue }) => {
    const res = await RestClient.postRequest(`${Customer_Admin_Users_Endpoints.bulkCreateUsers}`, queries)

    if (res.status >= 300) {
      return rejectWithValue(res)
    }

    return res.data
  },
)

export const getBulkCreateUsersIsValid = createAsyncThunk(
  'customerAdminUsers/getBulkCreateUsersIsValid',
  async (_, { rejectWithValue }) => {
    const res = await RestClient.getRequest(`${Customer_Admin_Users_Endpoints.bulkCreateUsersValidate}`)

    if (res.status >= 300) {
      return rejectWithValue(res)
    }

    return res.data
  },
)

export const getBulkCreateUsersProcessIsValid = createAsyncThunk(
  'customerAdminUsers/getBulkCreateUsersProcessIsValid',
  async (_, { rejectWithValue }) => {
    const res = await RestClient.getRequest(`${Customer_Admin_Users_Endpoints.bulkCreateUsersProcessValidate}`)

    if (res.status >= 300) {
      return rejectWithValue(res)
    }

    return res.data
  },
)
